import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BlockOutlined,
  Check,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FilterAltOffOutlined,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearFilters,
  setCustomFilter,
  setMarkerFilters,
} from "../features/assessorSlice";
import { markerOptionsWithLabels, NO_MARKER_COLOR } from "../markerColors";
import Legend from "./legend";

// A clickable color swatch: check when selected, dimmed when other markers
// are selected, legend meaning in the tooltip.
const MarkerDot = ({ color, label, active, anyActive, onClick, icon = null }) => (
  <Tooltip title={label} arrow>
    <IconButton size="small" onClick={onClick} sx={{ p: 0.5 }} aria-label={label}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          boxShadow: active ? `0 0 0 2px white, 0 0 0 4px ${color}` : "none",
          opacity: active || !anyActive ? 1 : 0.35,
          transition: "box-shadow 120ms, opacity 120ms",
        }}
      >
        {active ? <Check sx={{ fontSize: 15 }} /> : icon}
      </Box>
    </IconButton>
  </Tooltip>
);

const TextFilter = ({ def, filter, onChange }) => (
  <TextField
    fullWidth
    size="small"
    label={`${def.label} contains`}
    value={filter?.q || ""}
    onChange={(e) => onChange(e.target.value ? { q: e.target.value } : null)}
  />
);

const NumberFilter = ({ def, filter, onChange }) => {
  const update = (key, raw) => {
    const parsed = parseFloat(raw);
    const next = { ...(filter || {}) };
    if (Number.isFinite(parsed)) {
      next[key] = parsed;
    } else {
      delete next[key];
    }
    onChange(Object.keys(next).length ? next : null);
  };
  return (
    <Stack direction="row" spacing={1}>
      <TextField
        fullWidth
        size="small"
        type="number"
        label={`${def.label} min`}
        value={filter?.min ?? ""}
        onChange={(e) => update("min", e.target.value)}
      />
      <TextField
        fullWidth
        size="small"
        type="number"
        label="max"
        value={filter?.max ?? ""}
        onChange={(e) => update("max", e.target.value)}
      />
    </Stack>
  );
};

const DateFilter = ({ def, filter, onChange }) => {
  const update = (key, value) => {
    const next = { ...(filter || {}) };
    if (value) {
      next[key] = value;
    } else {
      delete next[key];
    }
    onChange(Object.keys(next).length ? next : null);
  };
  return (
    <Stack direction="row" spacing={1}>
      <TextField
        fullWidth
        size="small"
        type="date"
        label={`${def.label} from`}
        value={filter?.from || ""}
        onChange={(e) => update("from", e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        fullWidth
        size="small"
        type="date"
        label="to"
        value={filter?.to || ""}
        onChange={(e) => update("to", e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    </Stack>
  );
};

const SelectFilter = ({ def, filter, onChange }) => (
  <FormControl fullWidth size="small">
    <InputLabel>{def.label}</InputLabel>
    <Select
      multiple
      label={def.label}
      value={filter?.selected || []}
      onChange={(e) => {
        const selected = e.target.value;
        onChange(selected.length ? { selected } : null);
      }}
      renderValue={(selected) => selected.join(", ")}
    >
      {(def.options || []).map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const FILTER_CONTROLS = {
  text: TextFilter,
  number: NumberFilter,
  date: DateFilter,
  select: SelectFilter,
};

// Counts how many filters are active so the table can show it even when the
// editor panel (and this card) is hidden. Filters on archived/removed fields
// are excluded — the predicate ignores them too.
export const countActiveFilters = (filters, activeNames) =>
  (filters.markers.length ? 1 : 0) +
  Object.keys(filters.custom).filter((n) => !activeNames || activeNames.has(n)).length;

const FilterPanel = () => {
  const legend = useSelector((state) => state.cid.value.legend);
  const filters = useSelector((state) => state.assessor.value.filters);
  const propertyFieldDefs = useSelector((state) => state.settings.value.propertyFields);
  const propertyFields = useMemo(
    () => propertyFieldDefs.filter((f) => !f.archived),
    [propertyFieldDefs]
  );
  const activeFieldNames = useMemo(
    () => new Set(propertyFields.map((f) => f.internal_name)),
    [propertyFields]
  );
  const dispatch = useDispatch();
  const [legendOpen, setLegendOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);

  const markerOptions = markerOptionsWithLabels(legend);
  const activeCount = countActiveFilters(filters, activeFieldNames);

  const toggleMarker = (value) => {
    const next = filters.markers.includes(value)
      ? filters.markers.filter((m) => m !== value)
      : [...filters.markers, value];
    dispatch(setMarkerFilters(next));
  };

  return (
    <Card variant="outlined">
      {/* Override MUI's default 24px last-child bottom padding so the card
          hugs its content when the filters are collapsed. */}
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => setPanelOpen((prev) => !prev)}
              sx={{ cursor: "pointer", flexGrow: 1, minWidth: 0 }}
            >
              <Typography variant="subtitle1">Filters</Typography>
              {activeCount > 0 ? (
                <Chip size="small" color="primary" label={`${activeCount} active`} />
              ) : null}
            </Stack>
            {activeCount > 0 ? (
              <Button
                size="small"
                color="inherit"
                startIcon={<FilterAltOffOutlined />}
                onClick={() => dispatch(clearFilters())}
              >
                Clear
              </Button>
            ) : null}
            <IconButton
              size="small"
              onClick={() => setPanelOpen((prev) => !prev)}
              aria-label={panelOpen ? "Minimize filters" : "Expand filters"}
            >
              {panelOpen ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
            </IconButton>
          </Stack>

          <Collapse in={panelOpen}>
            {/* mt lives inside the Collapse so the header→content gap only
                takes space when expanded, not when collapsed. */}
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                flexWrap="wrap"
              >
                {markerOptions.map((option) => (
                  <MarkerDot
                    key={option.value}
                    color={option.color}
                    label={option.label}
                    active={filters.markers.includes(option.value)}
                    anyActive={filters.markers.length > 0}
                    onClick={() => toggleMarker(option.value)}
                  />
                ))}
                <MarkerDot
                  color={NO_MARKER_COLOR}
                  label="No marker"
                  active={filters.markers.includes("none")}
                  anyActive={filters.markers.length > 0}
                  onClick={() => toggleMarker("none")}
                  icon={<BlockOutlined sx={{ fontSize: 14 }} />}
                />
                {filters.markers.length > 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.75 }}>
                    {[
                      ...markerOptions
                        .filter((o) => filters.markers.includes(o.value))
                        .map((o) => o.label),
                      ...(filters.markers.includes("none") ? ["No marker"] : []),
                    ].join(", ")}
                  </Typography>
                ) : null}
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Edit marker legend
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setLegendOpen((prev) => !prev)}
                  aria-label={legendOpen ? "Hide legend editor" : "Show legend editor"}
                >
                  {legendOpen ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
                </IconButton>
              </Stack>
              <Collapse in={legendOpen}>
                <Legend compact />
              </Collapse>

              {propertyFields.length ? (
                <Stack spacing={1}>
                  {propertyFields.map((def) => {
                    const Control = FILTER_CONTROLS[def.field_type] || TextFilter;
                    return (
                      <Control
                        key={def.id}
                        def={def}
                        filter={filters.custom[def.internal_name]}
                        onChange={(filter) =>
                          dispatch(setCustomFilter({ name: def.internal_name, filter }))
                        }
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add custom property fields in Settings to unlock more filters.
                </Typography>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FilterPanel;
